import { RouteComponentProps } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { BackLink, Header } from 'src/components';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import { usePlaylistDetail, usePlaylistMutations } from 'src/features/AddToPlaylist';
import {
  playUrlForPlaylistItem,
  withPlaylistQueryParams,
} from 'src/features/AddToPlaylist/playlistPaths';
import { PlaylistItemsList } from './PlaylistItemsList';
import { PlaylistAddItemSearch } from './PlaylistAddItemSearch';

type Params = { id: string };

export default function PlaylistDetailView({
  match,
}: RouteComponentProps<Params>) {
  const id = match.params.id;
  const { data, isLoading } = usePlaylistDetail(id);
  const { updatePlaylist } = usePlaylistMutations();
  const history = useHistory();
  const [editOpen, setEditOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const sorted = useMemo(() => {
    if (!data?.items) return [];
    return [...data.items].sort((a, b) => a.position - b.position);
  }, [data?.items]);

  const playFirst = () => {
    const first = sorted[0];
    if (!first) return;
    history.push(withPlaylistQueryParams(playUrlForPlaylistItem(first), id, first.id));
  };

  const resumeItem = useMemo(() => {
    const lastId = data?.progress?.lastPlayedItemId;
    if (!lastId) return null;
    return sorted.find((i) => i.id === lastId) ?? null;
  }, [sorted, data?.progress?.lastPlayedItemId]);

  const playResume = () => {
    if (!resumeItem) return;
    history.push(withPlaylistQueryParams(playUrlForPlaylistItem(resumeItem), id, resumeItem.id));
  };

  const canEditMeta = data?.playlistType === 'custom';

  if (isLoading || !data) {
    return <Typography variant="body2">…</Typography>;
  }

  return (
    <>
      <BackLink label="Playlisty" to="/me/playlists" />
      <Header title={data.name} as="h1" />
      {data.description && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          {data.description}
        </Typography>
      )}
      <Typography variant="caption" display="block" sx={{ mb: 2 }}>
        {sorted.length ? `${sorted.length} pozycji` : 'Pusta playlista'}
      </Typography>

      <PlaylistAddItemSearch playlistId={id} existingItems={sorted} />

      {!sorted.length && (
        <Typography variant="body1" sx={{ mb: 2 }}>
          Wyszukaj treść powyżej albo dodaj ją z biblioteki sesji.
        </Typography>
      )}

      {canEditMeta && (
        <>
          <Button sx={{ mb: 2 }} onClick={() => {
            setName(data.name);
            setDescription(data.description ?? '');
            setEditOpen(true);
          }}>
            Edytuj
          </Button>
          <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Playlista</DialogTitle>
            <DialogContent>
              <TextField
                margin="dense"
                label="Nazwa"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Opis"
                fullWidth
                multiline
                minRows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditOpen(false)}>Anuluj</Button>
              <Button
                variant="contained"
                onClick={() => {
                  void updatePlaylist.mutateAsync({
                    id,
                    body: {
                      name: name.trim(),
                      description: description.trim() || undefined,
                    },
                  }).then(() => setEditOpen(false)).catch(() => {});
                }}
              >
                Zapisz
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {sorted.length > 0 && <PlaylistItemsList playlistId={id} items={data.items} />}

      {sorted.length >= 1 && (
        <>
          {resumeItem && resumeItem.id !== sorted[0]?.id && (
            <Button variant="contained" sx={{ mt: 2, mr: 1 }} onClick={playResume}>
              Kontynuuj
            </Button>
          )}
          <Button
            variant={resumeItem && resumeItem.id !== sorted[0]?.id ? 'outlined' : 'contained'}
            sx={{ mt: 2, mr: 1 }}
            onClick={playFirst}
          >
            Odtwórz od początku
          </Button>
        </>
      )}
    </>
  );
}
